import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { getApiBase } from "../api";

// ============================================================================
// CARRIER ASSIGNED LOADS — Mobile-Optimized
// Sticky header, stat chips, large-touch cards, one-tap status advance
// ============================================================================

function fmtAddr(addr, city, state) {
  if (addr && typeof addr === "object") {
    const parts = [addr.line1, addr.city, addr.state, addr.postal].filter(Boolean);
    return parts.join(", ") || `${city || "?"}, ${state || ""}`;
  }
  if (addr && typeof addr === "string") return addr;
  return `${city || "?"}, ${state || ""}`;
}

const STATUS_CONFIG = {
  BOOKED:      { accent: "#3b82f6", label: "Booked",      icon: "📝" },
  DISPATCHED:  { accent: "#8b5cf6", label: "Dispatched",  icon: "📤" },
  IN_TRANSIT:  { accent: "#22c55e", label: "In Transit",  icon: "🚛" },
  AT_PICKUP:   { accent: "#06b6d4", label: "At Pickup",   icon: "📦" },
  PICKED_UP:   { accent: "#10b981", label: "Picked Up",   icon: "✅" },
  AT_DELIVERY: { accent: "#f59e0b", label: "At Delivery", icon: "🏭" },
  DELIVERED:   { accent: "#22c55e", label: "Delivered",    icon: "✅" },
  CLOSED:      { accent: "#6b7280", label: "Closed",      icon: "📁" },
};

const NEXT_STATUS = {
  BOOKED: "DISPATCHED", DISPATCHED: "AT_PICKUP", AT_PICKUP: "PICKED_UP",
  PICKED_UP: "IN_TRANSIT", IN_TRANSIT: "AT_DELIVERY", AT_DELIVERY: "DELIVERED",
};

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { accent: "#6b7280", label: status, icon: "📋" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "5px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: `${c.accent}18`, color: c.accent,
      border: `1.5px solid ${c.accent}33`, whiteSpace: "nowrap",
    }}>
      {c.icon} {c.label}
    </span>
  );
}

function LoadCard({ load, onTap, onUpdateStatus }) {
  const c = STATUS_CONFIG[load.status] || { accent: "#6b7280" };
  const rate = load.rateAmountCents ? `$${(load.rateAmountCents / 100).toLocaleString()}` : "—";
  const next = NEXT_STATUS[load.status];
  const nextLabel = next ? STATUS_CONFIG[next]?.label : null;
  const showPOD = ["IN_TRANSIT", "AT_DELIVERY", "DELIVERED"].includes(load.status);

  return (
    <div style={{
      background: "#1e293b", borderRadius: 16, overflow: "hidden",
      borderLeft: `4px solid ${c.accent}`,
      marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    }}>
      <div onClick={onTap} style={{
        padding: "14px 14px 10px", cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}>
        {/* Top: load# + rate + status */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 17, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {load.referenceNumber || load.loadNumber || "—"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {load.equipmentType || "Dry Van"}{load.distanceMiles ? ` · ${load.distanceMiles} mi` : ""}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#22c55e", lineHeight: 1.2 }}>{rate}</div>
            <StatusBadge status={load.status} />
          </div>
        </div>

        {/* Route */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: 8, background: "#3b82f6" }} />
            <div style={{ width: 2, height: 18, background: "linear-gradient(#3b82f6, #ef4444)" }} />
            <div style={{ width: 8, height: 8, borderRadius: 8, background: "#ef4444" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fmtAddr(load.originAddress, load.originCity, load.originState)}
            </div>
            <div style={{ fontSize: 11, color: "#475569", margin: "2px 0" }}>
              {load.pickupStart ? new Date(load.pickupStart).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "TBD"}
              {" → "}
              {load.deliveryStart ? new Date(load.deliveryStart).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "TBD"}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fmtAddr(load.destAddress, load.destCity, load.destState)}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      {(next || showPOD) && (
        <div style={{ display: "flex", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {next && (
            <button onClick={(e) => { e.stopPropagation(); onUpdateStatus(load.id, next); }} style={{
              flex: 1, padding: "13px 8px", border: "none", cursor: "pointer",
              background: "rgba(34,197,94,0.06)", color: "#22c55e",
              fontSize: 13, fontWeight: 700,
              borderRight: showPOD ? "1px solid rgba(255,255,255,0.06)" : "none",
              WebkitTapHighlightColor: "transparent",
            }}>
              ➡️ {nextLabel}
            </button>
          )}
          {showPOD && (
            <button onClick={(e) => { e.stopPropagation(); onTap("pod"); }} style={{
              flex: 1, padding: "13px 8px", border: "none", cursor: "pointer",
              background: "rgba(239,68,68,0.06)", color: "#ef4444",
              fontSize: 13, fontWeight: 700,
              WebkitTapHighlightColor: "transparent",
            }}>
              📦 Capture POD
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CarrierAssignedLoads() {
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const apiBase = useMemo(() => getApiBase(), []);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("ACTIVE");
  const [statusMsg, setStatusMsg] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchLoads = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/carrier/loads/assigned`, {
        headers: { "x-carrier-email": carrierEmail || "tim@symplecode.com" },
      });
      const text = await res.text();
      if (text.trim().toLowerCase().startsWith("<!doctype") || text.includes("<html"))
        throw new Error("API returned HTML — backend may be down");
      const data = JSON.parse(text);
      setLoads(data.loads || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [apiBase, carrierEmail]);

  useEffect(() => { fetchLoads(); }, [fetchLoads]);

  const updateStatus = async (loadId, status) => {
    try {
      const res = await fetch(`${apiBase}/carrier/loads/${loadId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-carrier-email": carrierEmail || "tim@symplecode.com" },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setStatusMsg(`✅ Updated to ${STATUS_CONFIG[status]?.label || status}`);
        setTimeout(() => setStatusMsg(""), 3000);
        fetchLoads();
      }
    } catch (e) { setStatusMsg(`❌ ${e.message}`); }
  };

  const filteredLoads = useMemo(() => {
    if (filter === "ACTIVE") return loads.filter(l => !["DELIVERED", "CLOSED"].includes(l.status));
    if (filter === "DELIVERED") return loads.filter(l => ["DELIVERED", "CLOSED"].includes(l.status));
    return loads;
  }, [loads, filter]);

  const counts = useMemo(() => ({
    active: loads.filter(l => !["DELIVERED", "CLOSED"].includes(l.status)).length,
    transit: loads.filter(l => l.status === "IN_TRANSIT").length,
    delivered: loads.filter(l => ["DELIVERED", "CLOSED"].includes(l.status)).length,
    total: loads.length,
  }), [loads]);

  return (
    <div>
      {/* Sticky header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 60,
        background: "linear-gradient(180deg, #0f172a 0%, #0f172aee 100%)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "14px 14px 0",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#f1f5f9", letterSpacing: -0.5 }}>My Loads</div>
          <button onClick={() => fetchLoads(true)} disabled={refreshing} style={{
            width: 38, height: 38, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.04)", color: "#94a3b8", fontSize: 16,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            WebkitTapHighlightColor: "transparent",
          }}>
            <span style={{ display: "inline-block", animation: refreshing ? "carrier-spin 0.8s linear infinite" : "none" }}>🔄</span>
          </button>
        </div>

        {/* Stat chips */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[
            { n: counts.active, label: "Active", color: "#3b82f6" },
            { n: counts.transit, label: "Transit", color: "#22c55e" },
            { n: counts.delivered, label: "Done", color: "#64748b" },
          ].map(s => (
            <div key={s.label} style={{
              display: "flex", alignItems: "baseline", gap: 4,
              padding: "5px 10px", borderRadius: 10, background: `${s.color}12`,
            }}>
              <span style={{ fontSize: 17, fontWeight: 900, color: s.color }}>{s.n}</span>
              <span style={{ fontSize: 11, color: "#64748b" }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 4, paddingBottom: 10 }}>
          {[
            { key: "ACTIVE", label: `Active (${counts.active})` },
            { key: "DELIVERED", label: `Done (${counts.delivered})` },
            { key: "ALL", label: `All (${counts.total})` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)} style={{
              flex: 1, padding: "10px 6px", borderRadius: 10, border: "none",
              cursor: "pointer", fontSize: 13, fontWeight: filter === tab.key ? 700 : 500,
              background: filter === tab.key ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.04)",
              color: filter === tab.key ? "#ef4444" : "#64748b",
              WebkitTapHighlightColor: "transparent",
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Toast */}
      {statusMsg && (
        <div style={{
          margin: "8px 12px", padding: "10px 14px", borderRadius: 10,
          background: statusMsg.includes("❌") ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
          color: statusMsg.includes("❌") ? "#fca5a5" : "#86efac",
          fontSize: 13, fontWeight: 600,
        }}>
          {statusMsg}
        </div>
      )}

      {/* Card list */}
      <div style={{ padding: "6px 10px 20px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🚛</div>
            <div>Loading your loads...</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#fca5a5", background: "rgba(239,68,68,0.08)", borderRadius: 16 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
            <div style={{ marginBottom: 16 }}>{error}</div>
            <button onClick={() => fetchLoads()} style={{
              padding: "12px 24px", borderRadius: 12, border: "1px solid #ef4444",
              background: "transparent", color: "#ef4444", fontWeight: 700, cursor: "pointer",
            }}>Retry</button>
          </div>
        ) : filteredLoads.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
            <div style={{ fontSize: 48, marginBottom: 10 }}>{filter === "DELIVERED" ? "📦" : "🚛"}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>
              {filter === "DELIVERED" ? "No delivered loads yet" : "No active loads"}
            </div>
            <div style={{ fontSize: 13, marginBottom: 20 }}>
              {filter === "ACTIVE" ? "Check the Load Board for available freight" : "Complete some loads to see them here"}
            </div>
            {filter === "ACTIVE" && (
              <button onClick={() => navigate("/app/carrier/board")} style={{
                padding: "14px 28px", borderRadius: 14, border: "none",
                background: "#ef4444", color: "white", fontSize: 15, fontWeight: 700,
                cursor: "pointer", boxShadow: "0 4px 16px rgba(239,68,68,0.4)",
              }}>📋 Browse Load Board</button>
            )}
          </div>
        ) : (
          filteredLoads.map(load => (
            <LoadCard
              key={load.id}
              load={load}
              onTap={(action) => {
                if (action === "pod") navigate(`/app/carrier/loads/${load.id}/pod`);
                else navigate(`/app/carrier/loads/${load.id}`);
              }}
              onUpdateStatus={updateStatus}
            />
          ))
        )}
      </div>
      <style>{`@keyframes carrier-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
